// Solana adapter — SLIP-0010 ed25519 derivation and JSON-RPC reads.
//
// Solana is not an EVM chain and not a UTXO chain: an address is an ed25519
// public key, and a token balance does not live on the address itself but in a
// separate token account owned by it. Both differences are why this is its own
// adapter rather than a branch in an existing one.
//
// Derivation is SLIP-0010, NOT BIP-32: ed25519 has no non-hardened derivation,
// and the key schedule is a different HMAC chain. Using @scure/bip32 here would
// silently produce secp256k1 keys and addresses nobody can spend.

import { hmac } from '@noble/hashes/hmac';
import { sha512 } from '@noble/hashes/sha512';
import { ed25519 } from '@noble/curves/ed25519';
import { base58 } from '@scure/base';
import { TOKENS, requireTokenInfo, type TokenSymbol } from '@incpt/shared';
import type { Address, DerivedAddresses, IncomingTransfer } from '../types';

const HARDENED = 0x80000000;

/** One per mint is the honest case; more than this is not a wallet of ours. */
const MAX_TOKEN_ACCOUNTS_PER_MINT = 4;
/** Transaction reads in flight at once. Small enough not to trip rate limits. */
const READ_CONCURRENCY = 8;

/**
 * Phantom and Solflare derive account `i` at m/44'/501'/i'/0', so that is the
 * path we use: the same seed phrase imported there shows the same addresses.
 * A three-level m/44'/501'/i' is also in the wild and gives DIFFERENT keys —
 * changing this would orphan every address already registered.
 */
const PATH = (index: number) => [44 | HARDENED, 501 | HARDENED, index | HARDENED, 0 | HARDENED];

type Node = { key: Uint8Array; chainCode: Uint8Array };

function masterNode(seed: Uint8Array): Node {
  const I = hmac(sha512, new TextEncoder().encode('ed25519 seed'), seed);
  return { key: I.slice(0, 32), chainCode: I.slice(32) };
}

function deriveChild(parent: Node, index: number): Node {
  // Hardened only: 0x00 || parent private key || ser32(index). ed25519 cannot
  // do non-hardened derivation at all, so every path element is hardened.
  const data = new Uint8Array(1 + 32 + 4);
  data[0] = 0;
  data.set(parent.key, 1);
  new DataView(data.buffer).setUint32(33, index >>> 0, false);
  const I = hmac(sha512, parent.chainCode, data);
  return { key: I.slice(0, 32), chainCode: I.slice(32) };
}

/** SLIP-0010 ed25519 node at the given path. Exported for the spec vectors. */
export function slip10Ed25519(seed: Uint8Array, path: number[]): Node {
  let node = masterNode(seed);
  for (const index of path) node = deriveChild(node, index);
  return node;
}

/** Derive the Solana address + public key at the given account index. */
export function deriveSolanaAddresses(seed: Uint8Array, index = 0): DerivedAddresses {
  const node = slip10Ed25519(seed, PATH(index));
  const publicKey = ed25519.getPublicKey(node.key);
  return {
    // A Solana address IS the base58 of the 32-byte public key — no hashing,
    // no checksum, no version byte.
    eoa: base58.encode(publicKey),
    publicKey: base58.encode(publicKey),
  };
}

/** The 32-byte private key for signing, at the given account index. */
export function solanaPrivateKey(seed: Uint8Array, index = 0): Uint8Array {
  return slip10Ed25519(seed, PATH(index)).key;
}

/** Whether a string is a well-formed Solana address: base58 of exactly 32 bytes. */
export function isValidSolanaAddress(address: string): boolean {
  // Reject before decoding: base58 has no fixed length, so a 44-char limit
  // keeps a pathological input from being decoded at all.
  if (address.length < 32 || address.length > 44) return false;
  try {
    return base58.decode(address).length === 32;
  } catch {
    return false;
  }
}

async function rpc<T>(rpcUrl: string, method: string, params: unknown[]): Promise<T> {
  const res = await fetch(rpcUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ jsonrpc: '2.0', id: 1, method, params }),
  });
  if (!res.ok) throw new Error(`${method} HTTP ${res.status}`);
  const json = (await res.json()) as { result?: T; error?: { message: string } };
  if (json.error) throw new Error(`${method}: ${json.error.message}`);
  if (json.result === undefined) throw new Error(`${method}: no result`);
  return json.result;
}

type TokenAccountsResult = {
  value: Array<{
    pubkey: string;
    account: { data: { parsed: { info: { tokenAmount: { amount: string } } } } };
  }>;
};

/**
 * Raw SPL balance (token base units, 6 decimals for USDT/USDC on Solana).
 *
 * Summed across accounts, not read from one: a mint can have several token
 * accounts under the same owner, and reading only the canonical associated
 * account would under-report a balance someone sent to another of them.
 */
export async function getSolanaBalance(
  rpcUrl: string,
  address: Address,
  token: TokenSymbol,
): Promise<bigint> {
  const mint = requireTokenInfo('sol', token).address;
  const result = await rpc<TokenAccountsResult>(rpcUrl, 'getTokenAccountsByOwner', [
    address,
    { mint },
    { encoding: 'jsonParsed' },
  ]);
  let total = 0n;
  for (const acc of result.value ?? []) {
    const amount = acc.account?.data?.parsed?.info?.tokenAmount?.amount;
    if (amount) total += BigInt(amount);
  }
  return total;
}

/** Native SOL balance in lamports. */
export async function getSolanaNativeBalance(rpcUrl: string, address: Address): Promise<bigint> {
  const result = await rpc<{ value: number }>(rpcUrl, 'getBalance', [address]);
  return BigInt(result.value ?? 0);
}

/**
 * Whether the owner already has a token account for this mint.
 *
 * Relevant to funding, not just display: creating one costs rent in SOL, so a
 * user who has never held the token cannot receive it without someone paying
 * that rent first.
 */
export async function hasSolanaTokenAccount(
  rpcUrl: string,
  address: Address,
  token: TokenSymbol,
): Promise<boolean> {
  const result = await rpc<TokenAccountsResult>(rpcUrl, 'getTokenAccountsByOwner', [
    address,
    { mint: requireTokenInfo('sol', token).address },
    { encoding: 'jsonParsed' },
  ]);
  return (result.value ?? []).length > 0;
}

type ParsedInstruction = {
  program?: string;
  parsed?: {
    type?: string;
    info?: {
      destination?: string;
      source?: string;
      authority?: string;
      lamports?: number;
      mint?: string;
      tokenAmount?: { amount?: string; decimals?: number };
      amount?: string;
    };
  };
};

type ParsedTx = {
  blockTime?: number | null;
  transaction?: { signatures?: string[]; message?: { instructions?: ParsedInstruction[] } };
  meta?: { err?: unknown } | null;
};

/**
 * Incoming SOL and SPL transfers to `address`, newest first.
 *
 * Two things about Solana make this the most expensive scan we run, and both
 * were measured against mainnet rather than assumed:
 *
 * 1. There is no "transfers to this address" query, so we walk recent
 *    signatures and read each transaction.
 * 2. An SPL transfer does not appear on the WALLET's signature list at all —
 *    it names the token account. On a live exchange wallet, all four of its
 *    USDC token account's signatures were invisible on the wallet. Scanning
 *    only the wallet therefore misses every stablecoin deposit, silently. So
 *    we scan the wallet for coin transfers AND each of our token accounts for
 *    token transfers.
 *
 * `maxSignatures` bounds the work per scanned account. The caller dedupes on
 * `uniqueId`, so a bound that misses an older deposit only delays detection
 * until it is back inside the window; it never double-counts.
 *
 * Reading the transactions is the expensive part — tens of calls per address —
 * and the deposit poller sweeps every wallet on a timer, most of them idle.
 * So the newest signature of each scanned account forms a fingerprint, and an
 * unchanged fingerprint skips the transaction reads entirely. Listing
 * signatures still happens, which is what makes the check sound: if anything
 * landed on any of the accounts, the fingerprint moves.
 */
export async function getIncomingSolanaTransfers(
  rpcUrl: string,
  address: Address,
  maxSignatures = 25,
): Promise<IncomingTransfer[]> {
  // Our own token accounts, per mint. An incoming token transfer names one of
  // these as its destination, which is also how we tell direction: comparing
  // the destination to the wallet address would never match, so every transfer
  // the address took part in — its own outgoing ones included — would look
  // incoming.
  const ours = new Map<string, { sym: TokenSymbol; decimals: number }>();
  for (const [sym, info] of Object.entries(TOKENS.sol) as [
    TokenSymbol,
    { address: string; decimals: number },
  ][]) {
    try {
      const accounts = await rpc<TokenAccountsResult>(rpcUrl, 'getTokenAccountsByOwner', [
        address,
        { mint: info.address },
        { encoding: 'jsonParsed' },
      ]);
      // Capped: a wallet legitimately has one account per mint, and the few
      // that hold dozens are not ours — scanning each one multiplies the work
      // by its count. Measured on an exchange wallet: 10 signatures per
      // account became 207 transaction reads and 89 seconds.
      for (const acc of (accounts.value ?? []).slice(0, MAX_TOKEN_ACCOUNTS_PER_MINT)) {
        if (acc.pubkey) ours.set(acc.pubkey, { sym, decimals: info.decimals });
      }
    } catch {
      // One mint failing must not hide deposits of the others.
    }
  }

  // Signatures from the wallet and from every token account, de-duplicated so
  // a transaction touching both is read once.
  const signatures = new Set<string>();
  const newestPerAccount: string[] = [];
  for (const account of [address, ...ours.keys()]) {
    try {
      const sigs = await rpc<Array<{ signature: string; err?: unknown }>>(
        rpcUrl,
        'getSignaturesForAddress',
        [account, { limit: maxSignatures }],
      );
      newestPerAccount.push(`${account}=${sigs?.[0]?.signature ?? ''}`);
      for (const s of sigs ?? []) {
        if (!s.err) signatures.add(s.signature); // failed tx — nothing moved
      }
    } catch {
      // Degrade per account rather than losing the whole scan — and leave this
      // account out of the fingerprint so a failed listing cannot make an
      // unchanged one, which would skip the reads on the next pass too.
      newestPerAccount.push(`${account}=?${Date.now()}`);
    }
  }

  if (signatures.size === 0) return [];

  // Unchanged fingerprint → nothing new on any account, so skip the reads.
  const fingerprint = newestPerAccount.join('|');
  const cacheKey = `${rpcUrl}:${address}`;
  if (scanFingerprints.get(cacheKey) === fingerprint) return [];

  // Read in small batches rather than one at a time: the scan is latency-bound
  // and a sequential loop over a busy address took a minute and a half.
  const ordered = [...signatures];
  const fetched: Array<[string, ParsedTx | null]> = [];
  for (let i = 0; i < ordered.length; i += READ_CONCURRENCY) {
    const batch = ordered.slice(i, i + READ_CONCURRENCY);
    const results = await Promise.all(
      batch.map(async (signature) => {
        try {
          return [
            signature,
            await rpc<ParsedTx>(rpcUrl, 'getTransaction', [
              signature,
              { encoding: 'jsonParsed', maxSupportedTransactionVersion: 0 },
            ]),
          ] as [string, ParsedTx];
        } catch {
          return [signature, null] as [string, null]; // one unreadable tx must not hide the rest
        }
      }),
    );
    fetched.push(...results);
  }

  const out: IncomingTransfer[] = [];
  for (const [signature, tx] of fetched) {
    if (!tx || tx.meta?.err) continue;
    const ts = (tx.blockTime ?? 0) * 1000;
    const instructions = tx.transaction?.message?.instructions ?? [];

    for (let i = 0; i < instructions.length; i++) {
      const ix = instructions[i];
      const info = ix?.parsed?.info;
      if (!info) continue;
      const type = ix?.parsed?.type;

      if (ix?.program === 'system' && type === 'transfer' && info.lamports != null) {
        if (info.destination !== address) continue;
        if (info.source === address) continue; // own transfer between own accounts
        out.push({
          chain: 'sol',
          token: 'SOL',
          txHash: signature,
          // The signature alone would collapse several transfers in one tx, so
          // the instruction index goes into the key.
          uniqueId: `${signature}:${i}`,
          from: info.source ?? '',
          to: address,
          amount: formatUnits(BigInt(info.lamports), 9),
          timestamp: ts,
        });
        continue;
      }

      if (ix?.program !== 'spl-token') continue;
      if (type !== 'transfer' && type !== 'transferChecked') continue;
      // Matched on OUR token account, which also works for the unchecked
      // `transfer` variant that carries no mint at all.
      const meta = info.destination ? ours.get(info.destination) : undefined;
      if (!meta) continue;
      // Moving between our own accounts is not a deposit.
      if (info.authority === address || info.source === address) continue;
      if (info.source && ours.has(info.source)) continue;
      const raw = info.tokenAmount?.amount ?? info.amount;
      if (!raw) continue;
      out.push({
        chain: 'sol',
        token: meta.sym,
        txHash: signature,
        uniqueId: `${signature}:${i}`,
        from: info.authority ?? info.source ?? '',
        to: address,
        amount: formatUnits(BigInt(raw), meta.decimals),
        timestamp: ts,
      });
    }
  }
  out.sort((a, b) => b.timestamp - a.timestamp);
  // Recorded only after the reads succeeded, so a crash mid-scan re-reads
  // rather than skipping.
  scanFingerprints.set(cacheKey, fingerprint);
  return out;
}

/**
 * Per-process, so a restart simply re-reads once. Deliberately not persisted:
 * a stored cursor would be one more piece of state to migrate and reason
 * about, and the only cost of losing it is a single extra scan.
 */
const scanFingerprints = new Map<string, string>();

/** Drop the memo — for tests, and for an operator forcing a full re-read. */
export function resetSolanaScanCache(): void {
  scanFingerprints.clear();
}

type SignatureStatus = {
  err: unknown;
  confirmationStatus?: 'processed' | 'confirmed' | 'finalized' | null;
};

async function signatureStatus(
  rpcUrl: string,
  signature: string,
): Promise<SignatureStatus | null> {
  const res = await rpc<{ value: Array<SignatureStatus | null> }>(rpcUrl, 'getSignatureStatuses', [
    [signature],
    { searchTransactionHistory: true },
  ]);
  return res.value?.[0] ?? null;
}

/**
 * Whether a broadcast signature landed.
 *   'success' — confirmed or finalized, and the cluster did not reject it,
 *   'failed'  — the cluster executed it and it failed (nothing moved),
 *   'unknown' — not visible yet, only processed, or the RPC could not be read.
 * A signature that is merely processed can still be dropped when its blockhash
 * expires, so it is not success.
 */
export async function getSolanaTxResult(
  rpcUrl: string,
  signature: string,
): Promise<'success' | 'failed' | 'unknown'> {
  try {
    const status = await signatureStatus(rpcUrl, signature);
    if (!status) return 'unknown';
    if (status.err) return 'failed';
    if (status.confirmationStatus === 'confirmed' || status.confirmationStatus === 'finalized') {
      return 'success';
    }
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * The node answered and does not know this signature.
 * null when the RPC itself could not be read — absence is then not proof the
 * transaction died, only that we could not see it.
 */
export async function solanaSignatureMissing(
  rpcUrl: string,
  signature: string,
): Promise<boolean | null> {
  try {
    return (await signatureStatus(rpcUrl, signature)) == null;
  } catch {
    return null;
  }
}

/** Decimal string → base units. null when the string is not a clean decimal. */
function decimalToBase(amount: string, decimals: number): bigint | null {
  const [whole = '', fraction = ''] = amount.split('.');
  if (!/^\d+$/.test(whole) || !/^\d*$/.test(fraction) || fraction.length > decimals) return null;
  return (
    BigInt(whole) * 10n ** BigInt(decimals) +
    BigInt((fraction + '0'.repeat(decimals)).slice(0, decimals))
  );
}

async function tokenAccountOwner(
  rpcUrl: string,
  account: string,
): Promise<{ owner: string; mint: string } | null> {
  const res = await rpc<{
    value: { data?: { parsed?: { info?: { owner?: string; mint?: string } } } } | null;
  }>(rpcUrl, 'getAccountInfo', [account, { encoding: 'jsonParsed' }]);
  const info = res.value?.data?.parsed?.info;
  if (!info?.owner || !info.mint) return null;
  return { owner: info.owner, mint: info.mint };
}

/** The confirmed transaction moved `amount` lamports from `from` to `to`. */
export async function verifySolanaNativeTransfer(
  rpcUrl: string,
  signature: string,
  expected: { from: string; to: string; amountLamports: bigint },
): Promise<boolean | null> {
  try {
    const tx = await rpc<ParsedTx | null>(rpcUrl, 'getTransaction', [
      signature,
      { encoding: 'jsonParsed', maxSupportedTransactionVersion: 0, commitment: 'confirmed' },
    ]);
    if (!tx) return null;
    if (tx.meta?.err) return false;
    let paid = 0n;
    for (const ix of tx.transaction?.message?.instructions ?? []) {
      const info = ix.parsed?.info;
      if (ix.program !== 'system' || ix.parsed?.type !== 'transfer' || !info) continue;
      if (info.source !== expected.from || info.destination !== expected.to) continue;
      if (info.lamports == null) continue;
      paid += BigInt(info.lamports);
    }
    return paid === expected.amountLamports;
  } catch {
    return null;
  }
}

/**
 * The confirmed transaction moved the exact SPL amount from `from`'s authority
 * into a token account owned by `to` for this mint. The destination is checked
 * by owner rather than by the associated-token address, so a transfer into
 * another account of the same holder still counts.
 */
export async function verifySolanaTokenTransfer(
  rpcUrl: string,
  signature: string,
  expected: { token: TokenSymbol; from: string; to: string; amount: string },
): Promise<boolean | null> {
  try {
    const mint = requireTokenInfo('sol', expected.token);
    const want = decimalToBase(expected.amount, mint.decimals);
    if (want == null) return false;
    const tx = await rpc<ParsedTx | null>(rpcUrl, 'getTransaction', [
      signature,
      { encoding: 'jsonParsed', maxSupportedTransactionVersion: 0, commitment: 'confirmed' },
    ]);
    if (!tx) return null;
    if (tx.meta?.err) return false;

    const owners = new Map<string, { owner: string; mint: string } | null>();
    let paid = 0n;
    for (const ix of tx.transaction?.message?.instructions ?? []) {
      const info = ix.parsed?.info;
      const type = ix.parsed?.type;
      if (ix.program !== 'spl-token' || !info) continue;
      if (type !== 'transfer' && type !== 'transferChecked') continue;
      if (info.authority !== expected.from || !info.destination) continue;
      if (type === 'transferChecked' && info.mint !== mint.address) continue;
      const raw = info.tokenAmount?.amount ?? info.amount;
      if (!raw) continue;
      let account = owners.get(info.destination);
      if (account === undefined) {
        account = await tokenAccountOwner(rpcUrl, info.destination);
        owners.set(info.destination, account);
      }
      if (!account) return null;
      if (account.owner !== expected.to || account.mint !== mint.address) continue;
      paid += BigInt(raw);
    }
    return paid === want;
  } catch {
    return null;
  }
}

/** Decimal string from base units, without going through Number. */
function formatUnits(value: bigint, decimals: number): string {
  const negative = value < 0n;
  const digits = (negative ? -value : value).toString().padStart(decimals + 1, '0');
  const whole = digits.slice(0, digits.length - decimals);
  const fraction = digits.slice(digits.length - decimals).replace(/0+$/, '');
  return `${negative ? '-' : ''}${whole}${fraction ? `.${fraction}` : ''}`;
}
